import { Link } from 'react-router-dom';
import { useCompanyBranding, usePublicStats } from '../../hooks/unified';
import { useSEO, SEO_CONFIGS } from '../../hooks/useSEO';

// Minimum display values for when real data is low/empty
const MIN_DISPLAY_VALUES = {
  active_jobs: 25,
  total_companies: 12,
  total_placements: 150,
  total_candidates: 500,
  success_rate: 92,
};

export function AboutPage() {
  useSEO(SEO_CONFIGS.about);
  const branding = useCompanyBranding();
  const { data: stats } = usePublicStats();

  // Use real values if they exceed minimums, otherwise show minimums
  const displayStats = {
    active_jobs: Math.max(stats?.active_jobs || 0, MIN_DISPLAY_VALUES.active_jobs),
    total_companies: Math.max(stats?.total_companies || 0, MIN_DISPLAY_VALUES.total_companies),
    total_placements: Math.max(stats?.total_placements || 0, MIN_DISPLAY_VALUES.total_placements),
    total_candidates: Math.max(stats?.total_candidates || 0, MIN_DISPLAY_VALUES.total_candidates),
    success_rate: Math.max(stats?.success_rate || 0, MIN_DISPLAY_VALUES.success_rate),
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About {branding.name}
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              {branding.tagline || 'Connecting talented professionals with exceptional opportunities'}
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {branding.description || 
                  `At ${branding.name}, we believe that finding the right job should be an empowering experience. 
                  Our mission is to bridge the gap between talented professionals and forward-thinking companies, 
                  creating meaningful connections that drive careers and businesses forward.`}
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                We're committed to providing a seamless, transparent, and supportive hiring experience 
                for both candidates and employers. Every match we make is a step toward building 
                stronger teams and more fulfilling careers.
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4">
                  <div className="text-4xl font-bold text-primary-600 mb-2">
                    {displayStats.active_jobs}+
                  </div>
                  <div className="text-gray-600">Active Jobs</div>
                </div>
                <div className="text-center p-4">
                  <div className="text-4xl font-bold text-primary-600 mb-2">
                    {displayStats.total_candidates}+
                  </div>
                  <div className="text-gray-600">Candidates</div>
                </div>
                <div className="text-center p-4">
                  <div className="text-4xl font-bold text-primary-600 mb-2">
                    {displayStats.total_placements}+
                  </div>
                  <div className="text-gray-600">Placements</div>
                </div>
                <div className="text-center p-4">
                  <div className="text-4xl font-bold text-primary-600 mb-2">
                    {displayStats.success_rate}%
                  </div>
                  <div className="text-gray-600">Success Rate</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Integrity</h3>
              <p className="text-gray-600">
                We operate with complete transparency and honesty in all our interactions, 
                building trust with candidates and employers alike.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">People First</h3>
              <p className="text-gray-600">
                Every decision we make puts people at the center. We're not just filling positions; 
                we're building careers and teams.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Innovation</h3>
              <p className="text-gray-600">
                We continuously improve our platform and processes to provide the best 
                possible experience for everyone we serve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Whether you're looking for your next career opportunity or searching for top talent, 
            we're here to help you succeed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/jobs"
              className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Browse Jobs
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-gray-900 font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
